// Ejecución de una simulación por tandas, con progreso y cancelación. No depende del navegador:
// la usan el Worker, el respaldo en el hilo principal y las pruebas.
import { FlightSimulation, SimulationInputError, createWind, type SimulationConfig, type SimulationProgress } from '@openrocket-web/physics';
import type { Outcome, RunRequest } from './protocol';

export interface RunHooks {
  /** Pasos por tanda antes de ceder el control y notificar el progreso. */
  readonly chunk?: number;
  readonly onProgress?: (p: SimulationProgress) => void;
  /** Se consulta entre tandas. */
  readonly shouldCancel?: () => boolean;
  /** Cede el control para que se procesen mensajes y eventos (p. ej. la cancelación). */
  readonly yieldNow?: () => Promise<void>;
}

export const DEFAULT_CHUNK = 400;

export function buildConfig(req: RunRequest): SimulationConfig {
  const byId = new Map(req.motors.map((m) => [m.id, m]));
  return {
    rocket: req.rocket,
    motors: (id) => byId.get(id),
    launch: req.launch,
    wind: createWind({ speed: req.wind.speed, direction: req.wind.direction, turbulenceIntensity: req.wind.turbulenceIntensity, seed: req.wind.seed }),
    roughness: req.roughness,
    timeStep: req.timeStep,
    seed: req.seed,
    perturbation: req.perturbation,
    ...(req.maxTime === undefined ? {} : { maxTime: req.maxTime }),
  };
}

export async function execute(req: RunRequest, hooks: RunHooks = {}): Promise<Outcome> {
  let sim: FlightSimulation;
  try {
    sim = new FlightSimulation(buildConfig(req));
  } catch (e) {
    if (e instanceof SimulationInputError) return { kind: 'invalid', message: e.message, issues: e.issues };
    return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
  }
  const chunk = hooks.chunk ?? DEFAULT_CHUNK;
  try {
    while (!sim.advance(chunk)) {
      hooks.onProgress?.(sim.progress);
      if (hooks.yieldNow !== undefined) await hooks.yieldNow();
      if (hooks.shouldCancel?.() === true) sim.cancel();
    }
    hooks.onProgress?.(sim.progress);
    return { kind: 'result', result: sim.result() };
  } catch (e) {
    return { kind: 'error', message: e instanceof Error ? e.message : String(e) };
  }
}
